import { gql } from '@apollo/client';
import type { CloudinaryTransformations } from '@mnd-frontend/graphql-schema';
import Honeybadger from 'honeybadger-js';
import { useState } from 'react';
import { useCurrentPressroom } from '../contexts/CurrentPressroomContext';
import { useEnvironmentVariables } from '../contexts/EnvironmentVariablesContext';
import { useSyncCloudinaryImageMutation } from '../generated/hooks';
import type { SyncCloudinaryImageMutation } from '../generated/types';

gql`
  mutation SyncCloudinaryImage(
    $pressroomId: ID!
    $cloudinaryId: ID!
    $cloudinaryFormat: String
    $cloudinaryVersion: Int!
    $cloudinaryBytes: Int
    $cloudinaryWidth: Int
    $cloudinaryHeight: Int
    $fileName: String
    $displayTransformation: CloudinaryTransformations
  ) {
    imageSyncCloudinaryUpload(
      input: {
        pressroomId: $pressroomId
        cloudinaryId: $cloudinaryId
        cloudinaryVersion: $cloudinaryVersion
        cloudinaryFormat: $cloudinaryFormat
        cloudinaryBytes: $cloudinaryBytes
        cloudinaryWidth: $cloudinaryWidth
        cloudinaryHeight: $cloudinaryHeight
        fileName: $fileName
      }
    ) {
      error {
        message
      }
      image {
        id
        cloudinaryId
        resourceImageId
        url(transformation: $displayTransformation)
        croppedUrl: url
        coverImageGravity {
          x
          y
        }
      }
    }
  }
`;

export interface ImageUploadResponse {
  asset_id: string;
  public_id: string;
  version: number;
  version_id: string;
  signature: string;
  width: number;
  height: number;
  format: string;
  resource_type: 'image';
  created_at: string;
  tags: string[];
  bytes: number;
  type: 'upload';
  etag: string;
  placeholder: false;
  url: string;
  secure_url: string;
  access_mode: 'public';
  original_filename: string;
}

type ImageUploadErrorResponse = {
  error?: {
    message?: string;
  };
};
type CallFunction = (
  file: Blob,
  transformation?: CloudinaryTransformations,
) => Promise<SyncCloudinaryImageMutation | null>;

type State = {
  loading: boolean;
  error: boolean;
  data?: SyncCloudinaryImageMutation | null;
};

type Args = {
  onError?: (error?: string) => void;
  onCompleted?: (response: SyncCloudinaryImageMutation) => void;
};

const constructFormData = (
  file: Blob,
  sourceId: string,
  { apiKey, preset }: { apiKey: string; preset: string },
) => {
  const fd = new FormData();

  fd.append('api_key', apiKey);
  fd.append('timestamp', (Date.now() / 1000).toString(10));
  fd.append('upload_preset', preset);
  fd.append('resource_type', 'image');
  fd.append('file', file);
  fd.append('tags', [`source_${sourceId}`] as any);
  return fd;
};

export const uploadImageToCloudinary = async (
  file: any,
  sourceId: string,
  onError: () => void,
  mndAssets: { apiKey: string; preset: string; postUrl: string },
): Promise<null | ImageUploadResponse> => {
  const fd = constructFormData(file, sourceId, mndAssets);
  try {
    const response = await fetch(mndAssets.postUrl, {
      method: 'POST',
      body: fd,
    });
    if (!response.ok) {
      const returnValue = (await response.json()) as ImageUploadErrorResponse;
      console.error(returnValue);
      Honeybadger.notify(response);
      onError();
      return null;
    }
    const returnValue = (await response.json()) as ImageUploadResponse;
    return returnValue;
  } catch (e) {
    console.error(e);
    onError();
    return null;
  }
};

/**
 * Uploads a blob to cloudinary, and then lets backend know about the created image.
 */
export const useCloudinaryImageUpload = ({
  onError,
  onCompleted,
}: Args = {}): [CallFunction, State] => {
  const { mndAssets } = useEnvironmentVariables();
  const { sourceId, pressroomId } = useCurrentPressroom();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [syncDatabase, { loading: syncLoading, error: syncError, data }] =
    useSyncCloudinaryImageMutation({ onCompleted });

  return [
    async (file: Blob, transformation?: CloudinaryTransformations) => {
      setError(false);
      const fd = constructFormData(file, sourceId, mndAssets);

      setLoading(true);
      try {
        const response = await fetch(mndAssets.postUrl, {
          method: 'POST',
          body: fd,
        });
        setLoading(false);
        if (!response.ok) {
          const returnValue = (await response.json()) as ImageUploadErrorResponse;
          onError?.(returnValue?.error?.message ?? JSON.stringify(returnValue));
          setError(true);
          Honeybadger.notify(response);
          return null;
        }
        const returnValue = (await response.json()) as ImageUploadResponse;
        const gqlResponse = await syncDatabase({
          variables: {
            pressroomId,
            cloudinaryId: returnValue.public_id,
            cloudinaryVersion: returnValue.version,
            cloudinaryBytes: returnValue.bytes,
            cloudinaryFormat: returnValue.format,
            cloudinaryHeight: returnValue.height,
            cloudinaryWidth: returnValue.width,
            fileName: decodeURI(
              returnValue.original_filename.replace(/%(?![0-9A-Fa-f]{2})/g, '') ?? '',
            ),
            displayTransformation: transformation,
          },
        });
        return gqlResponse.data || null;
      } catch (e) {
        onError?.();
        console.error(e);
        setError(true);
        setLoading(false);
        return null;
      }
    },
    { loading: loading || syncLoading, error: error || !!syncError, data },
  ];
};
